/**
 * Single source of truth for ALL order status colors.
 * Import this everywhere — do NOT duplicate in other files.
 *
 * Colors are grouped by merchant_title (what the merchant sees).
 * Statuses that share the same merchant_title share the same color.
 */

// merchant_title: "New"
const NEW         = { bg: 'hsla(268,60%,60%,0.12)',  text: 'hsl(268,55%,52%)' };
// merchant_title: "Scheduled"
const SCHEDULED   = { bg: 'hsla(210,70%,55%,0.12)',  text: 'hsl(210,65%,45%)' };
// merchant_title: "Collecting"
const COLLECTING  = { bg: 'hsla(30,75%,55%,0.12)',   text: 'hsl(30,70%,42%)' };
// merchant_title: "Collected"
const COLLECTED   = { bg: 'hsla(95,55%,55%,0.13)',   text: 'hsl(95,50%,40%)' };
// merchant_title: "En Route" / "In Transit"
const EN_ROUTE    = { bg: 'hsla(195,70%,50%,0.12)',  text: 'hsl(195,65%,40%)' };
// merchant_title: "Out for Delivery"
const OUT_FOR_DEL = { bg: 'hsla(145,60%,48%,0.12)',  text: 'hsl(145,55%,38%)' };
// merchant_title: "Postponed"
const POSTPONED   = { bg: 'hsla(40,90%,55%,0.12)',   text: 'hsl(40,80%,40%)' };
// merchant_title: "On Hold" — on-hold, hold-resolved, retry-request, action-required ALL same color
const ON_HOLD     = { bg: 'hsla(35,85%,52%,0.12)',   text: 'hsl(35,75%,38%)' };
// merchant_title: "Exception"
const EXCEPTION   = { bg: 'hsla(0,80%,55%,0.12)',    text: 'hsl(0,70%,45%)' };
// merchant_title: "Delivered"
const DELIVERED   = { bg: 'hsla(152,60%,48%,0.12)',  text: 'hsl(152,60%,38%)' };
// merchant_title: "Partially Delivered"
const PART_DEL    = { bg: 'hsla(140,50%,48%,0.12)',  text: 'hsl(140,45%,38%)' };
// merchant_title: "Return Requested"
const RTO_REQ     = { bg: 'hsla(15,70%,55%,0.12)',   text: 'hsl(15,65%,42%)' };
// merchant_title: "Return Scheduled"
const RTO_SCHED   = { bg: 'hsla(10,65%,52%,0.12)',   text: 'hsl(10,60%,42%)' };
// merchant_title: "Returning to Origin"
const RTO_TRANSIT = { bg: 'hsla(355,55%,50%,0.12)',  text: 'hsl(355,50%,40%)' };
// merchant_title: "Returned to Warehouse"
const RTO_WH      = { bg: 'hsla(25,65%,50%,0.12)',   text: 'hsl(25,60%,42%)' };
// merchant_title: "Returned"
const RETURNED    = { bg: 'hsla(340,50%,52%,0.12)',  text: 'hsl(340,45%,42%)' };
// merchant_title: "Partially Returned"
const PART_RET    = { bg: 'hsla(330,45%,52%,0.12)',  text: 'hsl(330,40%,42%)' };
// Other merchant titles
const RESEND      = { bg: 'hsla(210,60%,55%,0.12)',  text: 'hsl(210,55%,42%)' };
const INVENTORY   = { bg: 'hsla(35,70%,52%,0.12)',   text: 'hsl(35,65%,40%)' };
const CANCELLED   = { bg: 'hsla(0,70%,55%,0.10)',    text: 'hsl(0,65%,45%)' };
const CLAIM_SETTL = { bg: 'hsla(152,55%,48%,0.12)',  text: 'hsl(152,50%,35%)' };

export const STATUS_HSL: Record<string, { bg: string; text: string }> = {
  // merchant_title: "New"
  'new':                          NEW,
  // merchant_title: "Scheduled"
  'scheduled':                    SCHEDULED,
  // merchant_title: "Collecting"
  'collecting':                   COLLECTING,
  'out-for-collecting':           COLLECTING,
  // merchant_title: "Collected"
  'collected':                    COLLECTED,
  // merchant_title: "En Route" (sorted, transferred, received-warehouse, received-carrier, in-transit)
  'sorted':                       EN_ROUTE,
  'transferred-carrier':          EN_ROUTE,
  'en-route':                     EN_ROUTE,
  'in-transit':                   EN_ROUTE,
  'received-warehouse':           EN_ROUTE,
  'received-carrier-warehouse':   EN_ROUTE,
  // merchant_title: "Out for Delivery"
  'out-for-delivery':             OUT_FOR_DEL,
  'out-for-collection':           OUT_FOR_DEL,
  // merchant_title: "Postponed"
  'postponed':                    POSTPONED,
  // merchant_title: "On Hold" — ALL on-hold-family statuses share same color
  'on-hold':                      ON_HOLD,
  'hold-resolved':                ON_HOLD,
  'retry-request':                ON_HOLD,
  'action-required':              ON_HOLD,
  // merchant_title: "Exception"
  'exception':                    EXCEPTION,
  // merchant_title: "Delivered"
  'delivered':                    DELIVERED,
  // merchant_title: "Partially Delivered"
  'partially-delivered':          PART_DEL,
  // merchant_title: "Return Requested"
  'rto-requested':                RTO_REQ,
  'rto-request-pending':          RTO_REQ,
  // merchant_title: "Return Scheduled"
  'rto-scheduled':                RTO_SCHED,
  // merchant_title: "Returning to Origin"
  'rto-collecting':               RTO_TRANSIT,
  'rto-in-transit':               RTO_TRANSIT,
  'rto-drop-off-scanned':         RTO_TRANSIT,
  // merchant_title: "Returned to Warehouse"
  'rto-received-warehouse':       RTO_WH,
  'rto-received-carrier-warehouse': RTO_WH,
  // merchant_title: "Returned"
  'rto-delivered':                RETURNED,
  // merchant_title: "Partially Returned"
  'rto-partially-delivered':      PART_RET,
  // Others
  'resend':                       RESEND,
  'inventory':                    INVENTORY,
  'cancelled':                    CANCELLED,
  'claim-settled':                CLAIM_SETTL,
};

export const getStatusColors = (slug: string): { bg: string; text: string } => {
  return STATUS_HSL[slug] ?? { bg: 'hsla(0,0%,50%,0.10)', text: 'hsl(0,0%,40%)' };
};
